import React, { useEffect, useState, useRef } from 'react';
import { BookOpen, Megaphone } from 'lucide-react';
import { fetchPublicAdConfig } from '../lib/telemetry';
import { getShadowId } from '../lib/localStore';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_48907742-bee5-4d15-a4b2-afd889071bde/artifacts/479zxh1m_image.png';
const ROTATE_MS = 7000;
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('http://', 'https://');

/**
 * Persistent looping ad strip — alternates between:
 *  - LoveAll logo card
 *  - "Advertise here — hello@theloveall.app" CTA
 *  - Admin-configured image+text (legacy single ad slot, kept for compat)
 *  - Multi-ad rotation pool from /api/telemetry/ads-active (new system).
 *    These are added FIRST so paying campaigns get airtime priority.
 */
export default function LoopingAdBanner({ variant = 'strip' }) {
  const [adConfig, setAdConfig] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  const seenRef = useRef(new Set()); // ad_ids whose impression has been logged

  useEffect(() => {
    fetchPublicAdConfig().then(setAdConfig);
    // Pull the multi-ad rotation pool. Soft-fail — banner still works on
    // legacy ad config alone if this endpoint isn't reachable.
    fetch(`${BACKEND_URL}/api/telemetry/ads-active`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.ads) setCampaigns(d.ads); })
      .catch(() => {});
  }, []);

  // Build the rotating slide list
  const slides = [];
  // Multi-ad campaigns first (they're paid)
  for (const c of campaigns) {
    slides.push({ kind: 'campaign', ad: c });
  }
  // Then legacy slot for backwards compat
  if (adConfig?.interval_image_url || adConfig?.interval_text || adConfig?.sticky_text) {
    slides.push({
      kind: 'admin',
      image: adConfig.interval_image_url,
      text: adConfig.interval_text || adConfig.sticky_text,
      url: adConfig.click_through_url,
    });
  }
  // House slides (always present)
  slides.push({ kind: 'logo' });
  slides.push({ kind: 'guide' });
  slides.push({ kind: 'cta' });

  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setSlideIdx(i => (i + 1) % slides.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [slides.length]);

  const current = slides[slideIdx % slides.length] || slides[0];

  // Track impressions when a campaign slide is shown for the first time per
  // session. Dedup via the seenRef so we don't double-count if the slot
  // re-renders.
  useEffect(() => {
    if (current?.kind !== 'campaign') return;
    const ad = current.ad;
    if (!ad?.ad_id || seenRef.current.has(ad.ad_id)) return;
    seenRef.current.add(ad.ad_id);
    fetch(`${BACKEND_URL}/api/telemetry/ads/${ad.ad_id}/impression`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shadow_id: getShadowId() }),
    }).catch(() => {});
  }, [current]);

  const handleCampaignClick = (ad) => (e) => {
    // Fire-and-forget click telemetry; don't block the navigation.
    fetch(`${BACKEND_URL}/api/telemetry/ads/${ad.ad_id}/click`, { method: 'POST' }).catch(() => {});
    if (!ad.click_url) {
      e.preventDefault();
      return;
    }
    // Allow default <a> behavior to navigate.
  };

  // Global ads kill-switch — secret-admin can hide every ad slot without
  // deleting the campaigns.
  if (adConfig && adConfig.ads_enabled === false) return null;

  // Shared shell
  const isInline = variant === 'inline';
  const shell = isInline
    ? 'rounded-xl border border-border bg-card/70 backdrop-blur'
    : 'rounded-xl border border-border bg-card/90 backdrop-blur shadow-lg';

  const inner = (() => {
    if (current.kind === 'campaign') {
      const ad = current.ad;
      // House LoveAll slide — render the logo treatment instead of the URL.
      if (ad.ad_id === 'house_loveall') {
        return (
          <div className="flex items-center justify-center px-3 py-1.5 bg-black" data-testid={`ad-slide-${ad.ad_id}`}>
            <img src={LOGO_URL} alt="LoveAll" className="h-14 w-auto object-contain" />
          </div>
        );
      }
      // House Advertise-Here slide — render the email CTA.
      if (ad.ad_id === 'house_advertise') {
        return (
          <a href={ad.click_url} className="block hover:opacity-90 transition-opacity" data-testid={`ad-slide-${ad.ad_id}`}>
            <div className="flex items-center justify-center gap-2 py-2 px-3">
              <Megaphone className="h-4 w-4 text-amber-500" />
              <p className="text-xs font-medium">
                Advertise here — <span className="text-amber-500 font-bold">hello@theloveall.app</span>
              </p>
            </div>
          </a>
        );
      }
      // Paid campaign — image + click telemetry
      return (
        <a
          href={ad.click_url || '#'}
          target={ad.click_url ? '_blank' : undefined}
          rel="noopener noreferrer"
          onClick={handleCampaignClick(ad)}
          className="block hover:opacity-90 transition-opacity"
          data-testid={`ad-slide-campaign-${ad.ad_id}`}
        >
          {ad.image_url ? (
            <img src={ad.image_url} alt={ad.name || 'Ad'} className="w-full h-14 object-cover" />
          ) : (
            <div className="flex items-center gap-3 py-2 px-3">
              <Megaphone className="h-4 w-4 text-amber-500 shrink-0" />
              <p className="text-xs text-left flex-1 truncate">{ad.name}</p>
            </div>
          )}
        </a>
      );
    }
    if (current.kind === 'logo') {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 bg-black" data-testid="ad-slide-logo">
          <img
            src={LOGO_URL}
            alt="LoveAll — Ultimate Badminton Companion"
            className="h-14 w-auto object-contain"
          />
        </div>
      );
    }
    if (current.kind === 'cta') {
      return (
        <div className="flex items-center justify-center gap-2 py-2 px-3" data-testid="ad-slide-cta">
          <Megaphone className="h-4 w-4 text-amber-500" />
          <p className="text-xs font-medium">
            Advertise here — <a href="mailto:hello@theloveall.app" className="text-amber-500 hover:underline font-bold">hello@theloveall.app</a>
          </p>
        </div>
      );
    }
    if (current.kind === 'guide') {
      return (
        <a
          href="https://bwfbadminton.com/news-single/2023/09/06/laws-of-badminton-explained"
          target="_blank"
          rel="noopener noreferrer"
          className="block hover:opacity-90 transition-opacity"
          data-testid="ad-slide-guide"
        >
          <div className="flex items-center gap-3 py-2 px-3 bg-gradient-to-r from-primary/15 via-primary/5 to-secondary/15">
            <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
              <BookOpen className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <p className="font-heading text-xs font-extrabold uppercase tracking-tight leading-tight">Ultimate Badminton Guide</p>
              <p className="text-[10px] text-muted-foreground truncate">Rules, scoring, tactics — read free →</p>
            </div>
          </div>
        </a>
      );
    }
    // admin
    const content = (
      <div className="flex items-center gap-3 py-2 px-3" data-testid="ad-slide-admin">
        {current.image ? (
          <img src={current.image} alt="Ad" className="h-9 w-14 rounded object-cover" />
        ) : (
          <Megaphone className="h-4 w-4 text-amber-500 shrink-0" />
        )}
        <p className="text-xs text-left flex-1 truncate">{current.text}</p>
      </div>
    );
    return current.url ? (
      <a href={current.url} target="_blank" rel="noopener noreferrer" className="block">{content}</a>
    ) : content;
  })();

  return (
    <div className={`w-full max-w-md mx-auto ${shell}`} data-testid="looping-ad-banner">
      <div className="overflow-hidden">{inner}</div>
      {slides.length > 1 && (
        <div className="flex justify-center gap-1 pb-1.5" aria-hidden="true">
          {slides.map((_, i) => (
            <span
              key={`ad-dot-${i}`}
              className={`block h-1 rounded-full transition-all ${
                i === slideIdx % slides.length ? 'w-4 bg-primary' : 'w-1 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
